/**
 * Bouton standard — la source unique du style des actions, pour toutes les apps. UTILISER
 * PARTOUT : toute classe `bg-mark`, `bg-red-500/10`, `border-red-500/40` écrite à la main dans
 * un panneau est un bug ; on importe ce `<Button>` à la place.
 *
 * LE DESSIN (Kayla, 02-11-2026, devant trois styles et quatre palettes : le bouton « A, plein,
 * avec un reflet », sur la palette « Joyaux ») : une pilule PLEINE de sa couleur, un dégradé
 * vertical à peine marqué, un reflet d'un pixel, une ombre courte et une lueur de sa teinte ;
 * `brightness(1.08)` au survol, enfoncé d'un pixel à l'appui (motion-safe). Recette dans
 * `Button.css` : UNE recette, les teintes viennent des jetons — bleu Cyrano ou or RishFans,
 * jour ou nuit, sans qu'une ligne change.
 *
 * ⛔ Le « verre » (contour + voile pâle dedans, le défaut du 28-10) est REFUSÉ par Kayla le
 * 02-11, comme l'outline + voile qu'elle refusait déjà : la prop `look` (plat / verre / neon)
 * est acceptée et ignorée, dépréciée. Un panneau qui imite un contour teinté à la main reste
 * un bug.
 *
 * Sémantique des variantes (refonte 2026-05-28 — « le bouton supprimé devrait être rouge, le
 * bouton enregistré devrait être blanc, la présence invité devrait être orange » — et 02-11 :
 * « Enregistrer » prend la marque) :
 *   - primary     : la MARQUE, pleine — Enregistrer, Ajouter, Valider, Continuer. Le défaut.
 *   - accent      : la marque aussi — les CTA brand (« Envoyer l'invitation », « Activer »).
 *   - destructive : le ROUGE de sens — Supprimer, Archiver, Déconnecter. Toujours plein.
 *   - succes      : le VERT de sens — Confirmer un paiement, Lancer, Marquer fait.
 *   - secondary   : le NEUTRE plein (blanc le jour, anthracite la nuit) — Annuler, Retour, Filtre.
 *   - ghost       : rien au repos, le neutre au survol — les boutons icône d'une cellule.
 *   Le texte posé dessus est le jeton `-fg` de la teinte. Désactivé : opacité 0,38.
 *
 * Tailles :
 *   - sm : px-2  py-1   text-[10px] — chips, icône seule dans une cellule
 *   - md : px-3  py-1.5 text-[11px] — défaut, pied d'une modale
 *   - lg : px-4  py-2   text-xs     — barres d'outils, gros CTA
 *   - xl : px-6  py-4   text-[16-17px], min-h 56-64px — CTA « arcade » plein écran (La Régie,
 *          2026-07-10) ; `className="w-full"` au call-site pour une pile pleine largeur.
 *
 * `enCours` (lot 4) : inactif, `aria-busy`, un anneau à la place de l'icône. `ref` : l'ancre
 * d'un Volet ou d'un Menu. `href` : un vrai `<a>` au même style.
 */
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLAttributeAnchorTarget,
  ReactNode,
  Ref,
} from "react";
import { cn } from "./utils";
import { Spinner } from "./Spinner";
import "./Button.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive" | "accent" | "succes";
export type ButtonSize = "sm" | "md" | "lg" | "xl";
/** @deprecated Plus aucun look depuis le 02-11-2026 : une seule recette, pleine. Accepté et ignoré. */
export type ButtonLook = "plat" | "verre" | "neon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Icône rendue à gauche du label. */
  icon?: ReactNode;
  /** Icône rendue à droite. */
  iconRight?: ReactNode;
  /** Un carré, sans padding horizontal — le bouton icône seule. */
  iconOnly?: boolean;
  /** Si fourni, un `<a href>` au même style : un vrai lien s'ouvre partout (navigateur ET
   *  webview Tauri), là où `window.open()` est parfois bloqué. */
  href?: string;
  /** La cible du lien (« _blank ») ; `rel` sécurisé posé tout seul pour « _blank ». */
  target?: HTMLAttributeAnchorTarget;
  rel?: string;
  /** @deprecated Ignoré depuis le 02-11-2026 (une seule recette, pleine). */
  look?: ButtonLook;
  /** Ça s'exécute : inactif, `aria-busy`, un anneau à la place de l'icône. */
  enCours?: boolean;
  /** L'élément rendu — l'ancre d'un `Volet` ou d'un `Menu`. */
  ref?: Ref<HTMLButtonElement>;
}

const BASE_CLASSES =
  "btn-plein btn-pilule inline-flex items-center justify-center transition focus:outline-none disabled:cursor-not-allowed";

/** La teinte de chaque variante (cf Button.css). */
const TEINTE: Record<ButtonVariant, string> = {
  primary: "btn-teinte-marque",
  accent: "btn-teinte-marque",
  destructive: "btn-teinte-rouge",
  succes: "btn-teinte-verte",
  secondary: "btn-teinte-neutre",
  ghost: "btn-teinte-fantome",
};

// L'échelle (CLAUDE.md, « L'échelle », 02-11-2026) : la MÊME hauteur que Champ, Select, les
// segments d'Onglets et le pied Pagination — sm 32, md 40, lg 48 — pour qu'une rangée s'aligne.
// Avant, un bouton md faisait ~28 px (du padding, pas de hauteur) à côté d'un champ de 40.
const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-[12px] gap-1.5",
  md: "h-10 px-4 text-[13px] gap-2",
  lg: "h-12 px-5 text-[15px] gap-2",
  // « Arcade » (La Régie, 2026-07-10) : CTA plein écran, texte 16-17 px, hauteur fluide
  // 56→64 px, hover-lift léger (motion-safe).
  xl: "min-h-14 px-6 py-4 text-[16px] gap-3 @[480px]/panel:min-h-16 @[480px]/panel:text-[17px] motion-safe:hover:-translate-y-0.5",
};

/** Le rond à icône seule : un carré de la hauteur de l'échelle, exactement celle de la pilule. */
const ICON_ONLY_SIZE: Record<ButtonSize, string> = {
  sm: "h-8 w-8 p-0",
  md: "h-10 w-10 p-0",
  lg: "h-12 w-12 p-0",
  xl: "h-14 w-14 p-0 @[480px]/panel:h-16 @[480px]/panel:w-16",
};

export function Button({
  variant = "primary",
  size = "md",
  icon,
  iconRight,
  iconOnly,
  className,
  children,
  type = "button",
  href,
  target,
  rel,
  disabled,
  look: _look,
  enCours = false,
  ref,
  ...rest
}: ButtonProps) {
  void _look;
  const classes = cn(
    BASE_CLASSES,
    TEINTE[variant],
    iconOnly ? ICON_ONLY_SIZE[size] : SIZE_CLASSES[size],
    // Les actions pleines en semi-gras, les discrètes (neutre, fantôme) en medium.
    variant === "secondary" || variant === "ghost" ? "font-medium" : "font-semibold",
    className,
  );

  // `children` est toujours rendu : `iconOnly` ne fait que contrôler la taille (un carré) et le
  // padding. Une icône passée en children (`<Button iconOnly><Trash2 /></Button>`) apparaît ici ;
  // la prop `icon` sans children rend l'icône seule.
  const anneau = enCours ? <Spinner taille="sm" ton="courant" decoratif /> : null;
  const content = (
    <>
      {anneau ?? icon}
      {iconOnly && enCours ? null : children}
      {iconRight}
    </>
  );

  // Mode lien : un <a> au même style. `disabled` n'existe pas sur un <a> : un lien inactif ou
  // en cours retombe sur le <button>.
  if (href !== undefined && !disabled && !enCours) {
    const computedRel = rel ?? (target === "_blank" ? "noopener noreferrer" : undefined);
    return (
      <a
        ref={ref as unknown as Ref<HTMLAnchorElement>}
        href={href}
        target={target}
        rel={computedRel}
        className={classes}
        data-taille={size}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </a>
    );
  }

  return (
    <button ref={ref} type={type} disabled={disabled || enCours} aria-busy={enCours || undefined} className={classes} data-taille={size} {...rest}>
      {content}
    </button>
  );
}
